import React from 'react';
import { DateEffetButton } from '../dateEffet/DateEffetDialog';
import { DateEffetLotBar, useSelectionLot } from '../dateEffet/DateEffetLot';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

import { Building, Search, RefreshCw, Loader2, FileText, Download, FileSpreadsheet, ShieldCheck, MapPin, Award, Eye, FolderInput, Package, Mail, QrCode, Wrench, PenLine, Sparkles } from 'lucide-react';

import { BACKEND_URL, formatDate, formatMoney, STATUS_COLORS } from './dosHelpers';
import { useDOSDomium } from './useDOSDomium';
import DOSDialogs from './DOSDialogs';
import { RefreshHistoryWidget } from './DOSRefreshHistory';
import { StatsCards, PackTrackingBadge, NominativeTrackingBadge, SignatureBadge, Pagination } from './DOSParts';
import { AttestationCard } from './DOSAttestationCard';
import DOMIUMAssistantDevis from '../DOMIUMAssistantDevis';
import DOMIUMIAJournal from '../DOMIUMIAJournal';
import SimulateursATraiter from '../SimulateursATraiter';

export default function DOSDomiumDashboard({ getAuthHeader, userRole }) {
  const ctx = useDOSDomium(getAuthHeader, userRole);
  const { activeTab, setActiveTab, stats, souscriptions, loading, page, setPage, pageSize, setPageSize, toggleSort, sortIcon, totalPages, total, searchQuery, setSearchQuery, statusFilter, setStatusFilter, attestations, attPage, setAttPage, attTotalPages, attTotal, attTypeFilter, setAttTypeFilter, exportLoading, refreshingCache, refreshingAll, lastRefreshJob, refreshHistory, showRefreshHistory, setShowRefreshHistory, pdfPreview: pdfPreview, sendEmail: sendEmail, setSendEmail: setSendEmail, rectifyModal: rectifyModal, setRectifyModal, nomSign: nomSign, setNomSign: setNomSign, gedLoading, isAdmin, isSuperAdmin, handleExportCSV, handleExportExcel, refreshAll, handleViewDomiumDoc, closePdfPreview: closePdfPreview, downloadFromPreview: downloadFromPreview, fetchRefreshHistory, handleRefreshCacheAll, handleRefreshCache, handleDownloadDomiumDoc, openSendEmailDialog, submitSendEmail: submitSendEmail, openNomSignDialog, submitNomSign: submitNomSign, handleClasserGED } = ctx;
  const lot = useSelectionLot();
  const refsPage = souscriptions.map((s) => s.reference);
  const toutCoche = refsPage.length > 0 && refsPage.every((r) => lot.coche('domium', r));

  return (
    <div className="space-y-6" data-testid="dos-domium-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2" data-testid="dos-domium-title">
            <Building className="h-7 w-7 text-violet-600" />
            Suivi Adhesions DOS DOMIUM
          </h2>
          <p className="text-sm text-gray-500 mt-1">Dommage Ouvrage Solidaire - DOMIUM</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {isAdmin && (
            <>
              <Button variant="outline" size="sm" onClick={handleExportCSV} disabled={exportLoading === 'csv'} data-testid="domium-export-csv-btn">
                {exportLoading === 'csv' ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Download className="h-4 w-4 mr-1" />}
                CSV
              </Button>
              <Button variant="outline" size="sm" onClick={handleExportExcel} disabled={exportLoading === 'excel'} data-testid="domium-export-excel-btn">
                {exportLoading === 'excel' ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <FileSpreadsheet className="h-4 w-4 mr-1" />}
                Excel
              </Button>
              <Button
                size="sm"
                onClick={handleRefreshCacheAll}
                disabled={refreshingAll}
                className="bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="domium-refresh-all-btn"
                title="Régénérer TOUS les PDFs de toutes les souscriptions DOS DOMIUM (templates à jour, purge caches)"
              >
                {refreshingAll
                  ? <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  : <RefreshCw className="h-4 w-4 mr-1" />}
                {refreshingAll ? 'Régénération en cours…' : 'Régénérer tout'}
              </Button>
            </>
          )}
          <Button variant="outline" size="sm" onClick={refreshAll} data-testid="domium-refresh-btn">
            <RefreshCw className="h-4 w-4 mr-1" /> Actualiser
          </Button>
        </div>
      </div>

      {/* Bandeau : dernier job de régénération globale */}
      {isAdmin && lastRefreshJob && (
        <div
          className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 flex flex-wrap items-center gap-x-4 gap-y-1"
          data-testid="domium-last-refresh-banner"
        >
          <span className="font-semibold">Dernière régénération globale :</span>
          <span>
            {lastRefreshJob.finished_at
              ? new Date(lastRefreshJob.finished_at).toLocaleString('fr-FR')
              : 'en cours…'}
          </span>
          <span>par <em>{lastRefreshJob.actor}</em></span>
          <span>•&nbsp;{lastRefreshJob.regenerated}/{lastRefreshJob.regenerated_total} PDFs</span>
          <span>•&nbsp;{lastRefreshJob.pdf_cache_deleted} cache MongoDB purgé(s)</span>
          <span>•&nbsp;{lastRefreshJob.r2_pack_deleted} clés R2 supprimées</span>
          {lastRefreshJob.regen_errors > 0 && (
            <span className="text-red-700">•&nbsp;{lastRefreshJob.regen_errors} erreur(s)</span>
          )}
          <button
            type="button"
            className="ml-auto inline-flex items-center gap-1 text-amber-800 hover:text-amber-900 font-semibold underline underline-offset-2 text-[11px]"
            onClick={() => {
              const next = !showRefreshHistory;
              setShowRefreshHistory(next);
              if (next) fetchRefreshHistory();
            }}
            data-testid="domium-refresh-history-toggle"
          >
            {showRefreshHistory ? 'Masquer l’historique' : 'Voir l’historique des 10 derniers jobs'}
          </button>
        </div>
      )}

      {/* Historique : 10 derniers jobs Régénérer Tout (audit RGPD/AMF) */}
      {isAdmin && showRefreshHistory && (
        <RefreshHistoryWidget jobs={refreshHistory} onRefresh={fetchRefreshHistory} />
      )}

      {/* Stats Cards */}
      {stats && <StatsCards stats={stats} />}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-gray-100" data-testid="domium-tabs">
          <TabsTrigger value="overview" data-testid="tab-overview">
            <FileText className="h-4 w-4 mr-1" /> Adhesions
          </TabsTrigger>
          <TabsTrigger value="attestations" data-testid="tab-attestations">
            <Award className="h-4 w-4 mr-1" /> Attestations signees
          </TabsTrigger>
          <TabsTrigger value="assistant-ia" data-testid="tab-assistant-ia">
            <Sparkles className="h-4 w-4 mr-1" /> Assistant IA Devis DOS DOMIUM
          </TabsTrigger>
        </TabsList>

        {/* Tab: Adhesions */}
        <TabsContent value="overview">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <CardTitle className="text-lg">
                  Liste des adhesions ({total})
                </CardTitle>
                <div className="flex gap-2 flex-wrap">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      placeholder="Rechercher..."
                      className="pl-9 w-56"
                      value={searchQuery}
                      onChange={e => { setSearchQuery(e.target.value); setPage(1); }}
                      data-testid="domium-search-input"
                    />
                  </div>
                  <Select value={statusFilter} onValueChange={v => { setStatusFilter(v); setPage(1); }}>
                    <SelectTrigger className="w-44" data-testid="domium-status-filter">
                      <SelectValue placeholder="Filtrer par statut" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous les statuts</SelectItem>
                      <SelectItem value="active">Actives</SelectItem>
                      <SelectItem value="en_attente_paiement">Att. paiement</SelectItem>
                      <SelectItem value="en_cours">En cours</SelectItem>
                      <SelectItem value="archivee">Archivees</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-violet-600" />
                </div>
              ) : souscriptions.length === 0 ? (
                <div className="text-center py-12 text-gray-500" data-testid="domium-empty">
                  Aucune adhesion trouvee
                </div>
              ) : (
                <>
                  {isSuperAdmin && <DateEffetLotBar selection={lot} onDone={() => refreshAll && refreshAll()} />}
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="domium-table">
                      <thead>
                        <tr className="border-b bg-gradient-to-r from-violet-50 to-gray-50">
                          {isSuperAdmin && <th className="w-8 pl-3"><input type="checkbox" className="h-4 w-4 accent-[#0a3d62]" checked={toutCoche} onChange={(e) => lot.toutes('domium', refsPage, e.target.checked)} data-testid="domium-lot-tout" /></th>}
                          <th className="text-left px-4 py-3 font-semibold text-violet-800 cursor-pointer select-none hover:text-violet-600" onClick={() => toggleSort('reference')} data-testid="sort-th-reference">
                            <span className="inline-flex items-center gap-1">Ref. Dossier {sortIcon('reference')}</span>
                          </th>
                          <th className="text-left px-4 py-3 font-semibold text-violet-800 cursor-pointer select-none hover:text-violet-600" onClick={() => toggleSort('adherent')} data-testid="sort-th-adherent">
                            <span className="inline-flex items-center gap-1">Adherent {sortIcon('adherent')}</span>
                          </th>
                          <th className="text-left px-4 py-3 font-semibold text-violet-800">Territoire</th>
                          <th className="text-center px-4 py-3 font-semibold text-violet-800 cursor-pointer select-none hover:text-violet-600" onClick={() => toggleSort('statut')} data-testid="sort-th-statut">
                            <span className="inline-flex items-center justify-center gap-1">Statut {sortIcon('statut')}</span>
                          </th>
                          <th className="text-right px-4 py-3 font-semibold text-violet-800 cursor-pointer select-none hover:text-violet-600" onClick={() => toggleSort('montant')} data-testid="sort-th-montant">
                            <span className="inline-flex items-center justify-end gap-1">Montant Global {sortIcon('montant')}</span>
                          </th>
                          <th className="text-center px-4 py-3 font-semibold text-violet-800">Signature</th>
                          <th className="text-center px-4 py-3 font-semibold text-violet-800">Attestations</th>
                          <th className="text-left px-4 py-3 font-semibold text-violet-800 cursor-pointer select-none hover:text-violet-600" onClick={() => toggleSort('date')} data-testid="sort-th-date">
                            <span className="inline-flex items-center gap-1">Date {sortIcon('date')}</span>
                          </th>
                          <th className="text-center px-4 py-3 font-semibold text-violet-800">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {souscriptions.map((s, idx) => (
                          <tr key={s.reference || idx} className={`border-b hover:bg-violet-50/40 transition-colors ${idx % 2 === 1 ? 'bg-gray-50/60' : ''}`} data-testid={`domium-row-${idx}`}>
                            {isSuperAdmin && <td className="pl-3"><input type="checkbox" className="h-4 w-4 accent-[#0a3d62]" checked={lot.coche('domium', s.reference)} onChange={() => lot.toggle('domium', s.reference)} data-testid={`domium-lot-check-${idx}`} /></td>}
                            <td className="px-4 py-3 font-mono text-xs text-violet-700 font-medium">{s.reference}</td>
                            <td className="px-4 py-3">
                              <div className="font-medium text-gray-900">{s.nom_complet || '-'}</div>
                              {s.email && <div className="text-xs text-gray-500">{s.email}</div>}
                              {s.qualite && <div className="text-xs text-gray-400 capitalize">{s.qualite}</div>}
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-1 text-gray-600">
                                <MapPin className="h-3 w-3" />
                                <span className="capitalize">{s.territoire || '-'}</span>
                              </div>
                              {s.type_operation && <div className="text-xs text-gray-400 capitalize">{s.type_operation.replace(/_/g, ' ')}</div>}
                            </td>
                            <td className="px-4 py-3 text-center">
                              <Badge className={`text-xs ${STATUS_COLORS[s.status_group] || STATUS_COLORS.autre}`}>
                                {s.status_label || s.status}
                              </Badge>
                            </td>
                            <td className="px-4 py-3 text-right font-medium">{formatMoney(s.prime_totale || s.paiement_montant)}</td>
                            <td className="px-4 py-3 text-center">
                              <SignatureBadge statut={s.signature_statut} />
                            </td>
                            <td className="px-4 py-3 text-center">
                              <div className="flex items-center justify-center gap-1">
                                {s.has_attestation_provisoire && (
                                  <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-amber-100 text-amber-800 text-xs rounded" title="Attestation provisoire">
                                    <ShieldCheck className="h-3 w-3" /> Prov.
                                  </span>
                                )}
                                {s.has_attestation_definitive && (
                                  <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-emerald-100 text-emerald-800 text-xs rounded" title="Attestation definitive">
                                    <ShieldCheck className="h-3 w-3" /> Def.
                                  </span>
                                )}
                                {!s.has_attestation_provisoire && !s.has_attestation_definitive && (
                                  <span className="text-xs text-gray-400">-</span>
                                )}
                                <PackTrackingBadge tracking={s.pack_tracking} idx={idx} />
                                <NominativeTrackingBadge tracking={s.nominative_tracking} idx={idx} />
                              </div>
                            </td>
                            <td className="px-4 py-3 text-xs text-gray-500">{formatDate(s.created_at)}</td>
                            <td className="px-4 py-3 text-center">
                              <div className="flex items-center justify-center gap-1">
                                {(s.has_attestation_provisoire || s.has_attestation_definitive || s.signature_statut === 'signed') ? (
                                  <Button
                                    size="sm"
                                    onClick={() => handleViewDomiumDoc(s.reference, s.has_attestation_definitive || s.signature_statut === 'signed' ? 'signed' : 'draft')}
                                    className={`text-xs h-7 px-2 ${s.has_attestation_definitive || s.signature_statut === 'signed' ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : 'bg-amber-500 hover:bg-amber-600 text-white'}`}
                                    data-testid={`domium-view-att-${idx}`}
                                  >
                                    <Eye className="h-3 w-3 mr-1" />
                                    {s.has_attestation_definitive || s.signature_statut === 'signed' ? 'Attestation' : 'Draft'}
                                  </Button>
                                ) : (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleViewDomiumDoc(s.reference, 'draft')}
                                    className="text-xs h-7 px-2 border-gray-300 text-gray-500"
                                    data-testid={`domium-view-draft-${idx}`}
                                  >
                                    <Eye className="h-3 w-3 mr-1" /> Draft
                                  </Button>
                                )}
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleViewDomiumDoc(s.reference, 'cpcg')}
                                  className="text-xs h-7 px-2 border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                                  data-testid={`domium-view-cpcg-${idx}`}
                                  title="Conditions Particulières + Conditions Générales + Note"
                                >
                                  <FileText className="h-3 w-3 mr-1" /> CP/CG
                                </Button>
                                <Button
                                  size="sm"
                                  onClick={() => handleViewDomiumDoc(s.reference, 'pack')}
                                  className="text-xs h-7 px-2 bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 text-white"
                                  data-testid={`domium-view-pack-${idx}`}
                                  title="Pack complet : Attestation + CP + CG + Note (1 seul PDF)"
                                >
                                  <Package className="h-3 w-3 mr-1" /> Pack complet
                                </Button>
                                {(s.signature_statut === 'signed' || s.is_acquitted || s.has_attestation_provisoire || s.has_attestation_definitive) && (
                                  <Button
                                    size="sm"
                                    onClick={() => handleViewDomiumDoc(s.reference, 'nominative')}
                                    className="text-xs h-7 px-2 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white"
                                    data-testid={`domium-view-nominative-${idx}`}
                                    title="Attestation nominative officielle — générée à partir de l'attestation et des CP/CG signés, avec QR code de vérification"
                                  >
                                    <Award className="h-3 w-3 mr-1" /> Attestation nominative
                                  </Button>
                                )}
                                {isAdmin && (s.signature_statut === 'signed' || s.is_acquitted || s.has_attestation_provisoire || s.has_attestation_definitive) && (
                                  s.nominative_signature_statut === 'signed' ? (
                                    <span
                                      className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-teal-100 text-teal-800 text-xs rounded font-medium"
                                      title="Attestation nominative signée électroniquement par le Maître d'ouvrage (OTP email vérifié)"
                                      data-testid={`domium-nom-signed-${idx}`}
                                    >
                                      <ShieldCheck className="h-3 w-3" /> Signée MO
                                    </span>
                                  ) : (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => openNomSignDialog(s.reference, s.email || '')}
                                      className="text-xs h-7 px-2 border-teal-300 text-teal-700 hover:bg-teal-50"
                                      data-testid={`domium-nom-sign-${idx}`}
                                      title={s.nominative_invitation_sent
                                        ? 'Lien de signature déjà envoyé — renvoyer au Maître d\'ouvrage'
                                        : "Envoyer l'attestation nominative au Maître d'ouvrage pour signature électronique (code OTP par email, lien valable 7 jours)"}
                                    >
                                      <PenLine className="h-3 w-3 mr-1" /> {s.nominative_invitation_sent ? 'Relancer signature' : 'Signature MO'}
                                    </Button>
                                  )
                                )}
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => openSendEmailDialog(
                                    s.reference,
                                    s.identite?.email || s.adherent_email || '',
                                    s.identite?.nom_dirigeant || s.identite?.raison_sociale || ''
                                  )}
                                  className="text-xs h-7 px-2 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                                  data-testid={`domium-send-mo-${idx}`}
                                  title="Envoyer le Pack complet par email au Maître d'ouvrage (lien sécurisé 7 jours)"
                                >
                                  <Mail className="h-3 w-3 mr-1" /> Envoyer au MO
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => window.open(`${BACKEND_URL}/verify/domium/${s.reference}`, '_blank', 'noopener,noreferrer')}
                                  className="text-xs h-7 px-2 border-cyan-300 text-cyan-700 hover:bg-cyan-50"
                                  data-testid={`domium-test-qr-${idx}`}
                                  title="Ouvrir la page publique de vérification du QR code dans un nouvel onglet"
                                >
                                  <QrCode className="h-3 w-3 mr-1" /> Tester QR
                                </Button>
                                {isAdmin && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleRefreshCache(s.reference)}
                                    disabled={refreshingCache === s.reference}
                                    className="text-xs h-7 px-2 border-amber-300 text-amber-700 hover:bg-amber-50"
                                    data-testid={`domium-refresh-cache-${idx}`}
                                    title="Régénérer tous les PDFs (Attestation, CP, CG, Pack) avec les templates à jour. Purge les caches MongoDB et R2."
                                  >
                                    {refreshingCache === s.reference
                                      ? <Loader2 className="h-3 w-3 animate-spin mr-1" />
                                      : <RefreshCw className="h-3 w-3 mr-1" />}
                                    Régénérer
                                  </Button>
                                )}
                                {isAdmin && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleClasserGED(s.reference, s.has_attestation_definitive || s.signature_statut === 'signed' ? 'signed' : 'draft')}
                                    disabled={gedLoading === `${s.reference}-${s.has_attestation_definitive || s.signature_statut === 'signed' ? 'signed' : 'draft'}`}
                                    className="text-xs h-7 px-2 border-violet-300 text-violet-700 hover:bg-violet-50"
                                    data-testid={`domium-ged-${idx}`}
                                  >
                                    {gedLoading === `${s.reference}-${s.has_attestation_definitive || s.signature_statut === 'signed' ? 'signed' : 'draft'}` ? <Loader2 className="h-3 w-3 animate-spin" /> : <FolderInput className="h-3 w-3 mr-0.5" />}
                                    GED
                                  </Button>
                                )}
                                {isSuperAdmin && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => setRectifyModal({ open: true, reference: s.reference })}
                                    className="text-xs h-7 px-2 border-red-300 text-red-700 hover:bg-red-50"
                                    title={
                                      s.signature_statut === 'signed'
                                        ? 'Rectifier un document signe (Attestation / CP / CG)'
                                        : (s.has_attestation_definitive || s.has_attestation_provisoire)
                                          ? 'Rectifier un document genere (avant signature definitive)'
                                          : 'Modifier les donnees de cette adhesion (pre-generation)'
                                    }
                                    data-testid={`domium-rectify-${idx}`}
                                  >
                                    <Wrench className="h-3 w-3 mr-0.5" />
                                    Rectifier
                                  </Button>
                                )}
                                {isSuperAdmin && (
                                  <DateEffetButton produit="domium" ident={s.reference} compact onDone={() => refreshAll && refreshAll()} testId={`domium-date-effet-${idx}`} />
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <Pagination page={page} totalPages={totalPages} setPage={setPage}
                              total={total} pageSize={pageSize} setPageSize={setPageSize} />
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Attestations signees */}
        <TabsContent value="attestations">
          <Card>
            <CardHeader className="pb-3 bg-gradient-to-r from-violet-50 to-white">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Award className="h-5 w-5 text-violet-600" />
                  Attestations signees ({attTotal})
                </CardTitle>
                <Select value={attTypeFilter} onValueChange={v => { setAttTypeFilter(v); setAttPage(1); }}>
                  <SelectTrigger className="w-52" data-testid="att-type-filter">
                    <SelectValue placeholder="Type attestation" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Toutes</SelectItem>
                    <SelectItem value="provisoire">Provisoires</SelectItem>
                    <SelectItem value="definitive">Definitives</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent>
              {attestations.length === 0 ? (
                <div className="text-center py-12 text-gray-500" data-testid="att-empty">
                  Aucune attestation trouvee
                </div>
              ) : (
                <>
                  <div className="space-y-3">
                    {attestations.map((a, idx) => (
                      <AttestationCard key={a.reference || idx} item={a} idx={idx} onView={handleViewDomiumDoc} onDownload={handleDownloadDomiumDoc} onSendEmail={openSendEmailDialog} onSendNominativeSign={isAdmin ? openNomSignDialog : null} onClasserGED={isAdmin ? handleClasserGED : null} gedLoading={gedLoading} />
                    ))}
                  </div>
                  <Pagination page={attPage} totalPages={attTotalPages} setPage={setAttPage} total={attTotal} pageSize={20} />
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Assistant IA Devis */}
        <TabsContent value="assistant-ia" forceMount className="data-[state=inactive]:hidden">
          <DOMIUMAssistantDevis />
          <SimulateursATraiter />
          <DOMIUMIAJournal />
        </TabsContent>
      </Tabs>      <DOSDialogs ctx={ctx} />
    </div>
  );
}
